// UI locale configuration.
// Note: 'pt' = European Portuguese (pt-PT). This is intentional — the target
// audience is Portugal, not Brazil. Translation scripts and Clerk localizations
// should target pt-PT accordingly. 'fr' = French (fr-FR).

export const locales = ['en', 'es', 'pt', 'fr'] as const;
export type Locale = (typeof locales)[number];

export const defaultLocale: Locale = 'en';

export const localeLabels: Record<Locale, string> = {
  en: 'English',
  es: 'Español',
  pt: 'Português',
  fr: 'Français',
};

// ISO 3166-1 alpha-2 country code per locale, used to render a flag emoji in
// the language switcher (via getFlagEmoji). 'en' → GB.
export const localeCountryCodes: Record<Locale, string> = {
  en: 'GB',
  es: 'ES',
  pt: 'PT',
  fr: 'FR',
};

// BCP-47 tag per locale, used for locale-aware date/number formatting
// (Intl.*). English is en-GB (day-first, matches the GB flag + European
// audience); pt is European Portuguese (pt-PT), not Brazilian.
export const localeIntlTags: Record<Locale, string> = {
  en: 'en-GB',
  es: 'es-ES',
  pt: 'pt-PT',
  fr: 'fr-FR',
};

/** Returns true if the given string is a supported Locale. */
export function isSupportedLocale(value: string): value is Locale {
  return (locales as readonly string[]).includes(value);
}

/**
 * Maps a BCP-47 language tag (e.g. from navigator.language) to the nearest
 * supported Locale, or null if no match.
 */
export function matchLocale(tag: string): Locale | null {
  const lower = tag.toLowerCase();
  // Exact match first
  if (isSupportedLocale(lower)) return lower;
  // Prefix match (e.g. 'en-US' → 'en', 'pt-PT' → 'pt', 'es-419' → 'es')
  const prefix = lower.split('-')[0];
  if (isSupportedLocale(prefix)) return prefix;
  return null;
}
